import { ExternalLink, Github } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

export default function About() {
	return (
		<div className="container mx-auto py-8 px-4 max-w-4xl">
			<div className="mb-8">
				<h1 className="text-4xl font-bold mb-2">About Genius Fetch</h1>
				<p className="text-muted-foreground">
					Production-ready Rails + React application showcasing resilient API
					integration patterns
				</p>
			</div>

			<Card className="mb-6">
				<CardHeader>
					<CardTitle>Repository</CardTitle>
					<CardDescription>View the source code on GitHub</CardDescription>
				</CardHeader>
				<CardContent>
					<a
						href="https://github.com/kucho/genius_fetch"
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center gap-2 text-primary hover:underline"
					>
						<Github className="h-5 w-5" />
						github.com/kucho/genius_fetch
						<ExternalLink className="h-4 w-4" />
					</a>
				</CardContent>
			</Card>

			<Card className="mb-6">
				<CardHeader>
					<CardTitle>Core Feature</CardTitle>
				</CardHeader>
				<CardContent>
					<p className="mb-2">
						<strong>Input:</strong> Artist name
					</p>
					<p className="mb-4">
						<strong>Output:</strong> Complete list of all songs by that artist
					</p>
					<p className="text-sm text-muted-foreground">
						Search for artists and view their complete discography with metadata
						like page views, release dates, and artist information.
					</p>
				</CardContent>
			</Card>

			<Card className="mb-6">
				<CardHeader>
					<CardTitle>Technology Stack</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<h3 className="font-semibold mb-2">Backend</h3>
							<div className="flex flex-wrap gap-2">
								<Badge variant="secondary">Ruby on Rails 8</Badge>
								<Badge variant="secondary">SQLite</Badge>
								<Badge variant="secondary">Literal (type safety)</Badge>
								<Badge variant="secondary">Alba + Typelizer</Badge>
							</div>
						</div>

						<Separator />

						<div>
							<h3 className="font-semibold mb-2">Frontend</h3>
							<div className="flex flex-wrap gap-2">
								<Badge variant="secondary">React 19</Badge>
								<Badge variant="secondary">TypeScript</Badge>
								<Badge variant="secondary">Inertia.js</Badge>
								<Badge variant="secondary">shadcn/ui</Badge>
								<Badge variant="secondary">Tailwind CSS</Badge>
								<Badge variant="secondary">Vite</Badge>
							</div>
						</div>

						<Separator />

						<div>
							<h3 className="font-semibold mb-2">Testing</h3>
							<div className="flex flex-wrap gap-2">
								<Badge variant="secondary">Minitest</Badge>
								<Badge variant="secondary">VCR</Badge>
								<Badge variant="secondary">47 tests, 92 assertions</Badge>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card className="mb-6">
				<CardHeader>
					<CardTitle>Design Highlights</CardTitle>
				</CardHeader>
				<CardContent>
					<ul className="space-y-2 text-sm">
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Open/Closed Principle:</strong> Add new API endpoints
								without modifying existing code
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Type Safety:</strong> Literal::Struct for compile-time
								validation
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Polymorphism:</strong> Failure types instead of
								conditionals
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Adapter Pattern:</strong> Isolate chaos engineering from
								business logic
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Interactor Pattern:</strong> Extract business logic from
								controllers
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Persistent Connections:</strong> HTTPAdapter reuses
								TCP/TLS connections (60s keep-alive)
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>Chaos Engineering:</strong> Test resilience under
								controlled failure scenarios
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								<strong>VCR Testing:</strong> Deterministic API testing with
								real responses
							</span>
						</li>
					</ul>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Error Handling</CardTitle>
					<CardDescription>
						Production-ready resilience patterns
					</CardDescription>
				</CardHeader>
				<CardContent>
					<ul className="space-y-2 text-sm">
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>Timeouts: 5s open, 10s read on all connections</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>Safe JSON parsing with error handling</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>
								Custom error hierarchy (Timeout, RateLimit, ServerError)
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>Graceful degradation with user-friendly messages</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="text-primary">•</span>
							<span>Persistent connections reduce handshake overhead</span>
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
	);
}
